/*
 * Ported from Extend's document viewers (https://www.extend.ai/ui,
 * github.com/extend-hq/ui). Copyright (c) CrowdView Inc (Extend), MIT License.
 */
import type { ComponentProps, ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Separator } from "@volca/ui/components/ui/separator";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";

/**
 * A viewer's outer box: toolbar on top, the file below it, filling the
 * height it is given and scrolling inside.
 */
export const ViewerFrame = ({
  className,
  children,
  ...props
}: ComponentProps<"div">) => (
  <div
    data-slot="document-viewer"
    className={cn(
      "flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-xl border border-line bg-surface-raised",
      className,
    )}
    {...props}
  >
    <TooltipProvider>{children}</TooltipProvider>
  </div>
);

/** The row of controls above the file. */
export const ViewerToolbar = ({
  start,
  end,
}: {
  start?: ReactNode;
  end?: ReactNode;
}) => (
  <Stack
    direction="row"
    align="center"
    justify="between"
    gap={2}
    wrap
    className="min-h-11 shrink-0 border-b border-line px-2 py-1.5"
  >
    <Stack direction="row" align="center" gap={1} className="min-w-0">
      {start}
    </Stack>
    <Stack direction="row" align="center" gap={1} className="ml-auto min-w-0">
      {end}
    </Stack>
  </Stack>
);

/** The thin line between groups of toolbar controls. */
export const ToolbarDivider = () => (
  <Separator orientation="vertical" className="mx-1 h-4 self-center" />
);

/** An icon button with its name in a tooltip. */
export const ToolbarIconButton = ({
  label,
  children,
  ...props
}: Omit<ComponentProps<typeof Button>, "aria-label"> & { label: string }) => (
  <Tooltip>
    <TooltipTrigger
      render={
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          {...props}
        />
      }
    >
      {children}
    </TooltipTrigger>
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);

/** The body while the file loads, or when it cannot be shown. */
export const ViewerStatus = ({
  state,
  message,
}: {
  state: "loading" | "error";
  message?: string;
}) =>
  state === "loading" ? (
    <Spinner
      layout="centered"
      aria-label="Loading the file"
      className="min-h-52 flex-1 bg-surface-sunken"
    />
  ) : (
    <Stack
      align="center"
      justify="center"
      className="min-h-52 flex-1 bg-surface-sunken p-6 text-center"
    >
      <Text tone="muted" className="max-w-sm">
        {message ?? "This file could not be shown."}
      </Text>
    </Stack>
  );
